"use client";

import { useEffect, useRef } from "react";
import Link from "next/link";
import Icon from "@/components/ui/AppIcon";
import { textList, type PageTexts } from "@/lib/siteTexts";

// Wording comes from Admin -> Тексты страниц (storage.*).
const storageStyles = [
  { id: "block", icon: "ServerStackIcon", color: "#4F7CFF" },
  { id: "file", icon: "FolderOpenIcon", color: "#7C3AED" },
  { id: "object", icon: "CubeIcon", color: "#00FF88" },
] as const;

type Texts = PageTexts<"storage">;

/** Per GB-month, from the panel's prices; null when the panel has no price for it. */
export interface StoragePrices {
  block: string | null;
  file: string | null;
  object: string | null;
}

export default function StorageComparison({ prices, texts: t }: { prices: StoragePrices; texts: Texts }) {
  const storageTypes = storageStyles.map((style) => {
    const id = style.id;
    return {
      ...style,
      name: t[`types.${id}.name`],
      subtitle: t[`types.${id}.subtitle`],
      description: t[`types.${id}.description`],
      specs: ([1, 2, 3, 4, 5] as const).map((n) => [t[`types.${id}.spec${n}.label`], t[`types.${id}.spec${n}.value`]] as const),
      useCases: textList(t[`types.${id}.useCases`], "\n"),
      priceNote: t[`types.${id}.priceNote`],
    };
  });
  const tableRows = ([1, 2, 3, 4, 5, 6] as const).map((n) => ({
    param: t[`table.row${n}.param`],
    block: t[`table.row${n}.block`],
    file: t[`table.row${n}.file`],
    object: t[`table.row${n}.object`],
  }));
  const missing = t["table.price.missing"];
  const sectionRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.querySelectorAll(".reveal").forEach((el, i) => {
              setTimeout(() => el.classList.add("visible"), i * 100);
            });
          }
        });
      },
      { threshold: 0.05 }
    );
    if (sectionRef.current) observer.observe(sectionRef.current);
    return () => observer.disconnect();
  }, []);

  return (
    <section ref={sectionRef} className="py-24 bg-[#050B18] relative overflow-hidden">
      <div className="absolute inset-0 grid-bg opacity-30" />
      <div className="absolute top-1/3 left-0 w-96 h-96 bg-[rgba(79,124,255,0.05)] rounded-full blur-[120px] pointer-events-none" />

      <div className="max-w-7xl mx-auto px-6 relative z-10">
        {/* Header */}
        <div className="text-center mb-16 reveal">
          <span className="section-label">{t["types.label"]}</span>
          <h2 className="font-display font-bold text-4xl lg:text-5xl text-white mt-3 mb-4">
            {t["types.title"]}{" "}
            <span className="gradient-text">{t["types.titleAccent"]}</span>
          </h2>
          <p className="text-[#8BA3CC] text-lg max-w-2xl mx-auto">
            {t["types.subtitle"]}
          </p>
        </div>

        {/* Cards */}
        <div className="grid lg:grid-cols-3 gap-6 mb-20">
          {storageTypes.map((storage, idx) => (
            <div
              key={storage.id}
              className="glass rounded-2xl overflow-hidden card-hover reveal"
              style={{ animationDelay: `${idx * 100}ms` }}
            >
              {/* Card header */}
              <div
                className="p-7 border-b border-[rgba(79,124,255,0.1)] relative overflow-hidden"
                style={{ background: `${storage.color}08` }}
              >
                <div
                  className="absolute top-0 right-0 w-32 h-32 rounded-full blur-[60px] opacity-20"
                  style={{ background: storage.color }}
                />
                <div
                  className="w-14 h-14 rounded-xl flex items-center justify-center mb-5"
                  style={{ background: `${storage.color}18` }}
                >
                  <Icon name={storage.icon} size={28} style={{ color: storage.color }} />
                </div>
                <h3 className="font-display font-bold text-2xl text-white mb-1">{storage.name}</h3>
                <span
                  className="text-xs font-bold uppercase tracking-wider"
                  style={{ color: storage.color }}
                >
                  {storage.subtitle}
                </span>
                <p className="text-[#8BA3CC] text-sm leading-relaxed mt-3">{storage.description}</p>
              </div>

              {/* Specs */}
              <div className="p-7 border-b border-[rgba(79,124,255,0.1)]">
                <h4 className="text-white font-semibold text-sm mb-4">{t["types.specsTitle"]}</h4>
                <div className="space-y-2.5">
                  {storage.specs.map(([key, val]) => (
                    <div key={key} className="flex items-center justify-between">
                      <span className="text-[#4A6080] text-xs">{key}</span>
                      <span className="text-[#E8F0FF] text-xs font-medium">{val}</span>
                    </div>
                  ))}
                </div>
              </div>

              {/* Use cases */}
              <div className="p-7 border-b border-[rgba(79,124,255,0.1)]">
                <h4 className="text-white font-semibold text-sm mb-4">{t["types.useCasesTitle"]}</h4>
                <ul className="space-y-2">
                  {storage.useCases.map((uc) => (
                    <li key={uc} className="flex items-start gap-2 text-xs text-[#8BA3CC]">
                      <Icon name="CheckIcon" size={12} className="flex-shrink-0 mt-0.5" style={{ color: storage.color }} />
                      {uc}
                    </li>
                  ))}
                </ul>
              </div>

              {/* Pricing */}
              <div className="p-7 flex items-center justify-between">
                <div>
                  <div className="text-xl font-bold" style={{ color: storage.color }}>
                    {prices[storage.id]
                      ? `${storage.id === "file" ? t["types.priceFrom"] : ""}${prices[storage.id]}${t["types.priceUnit"]}`
                      : t["types.priceMissing"]}
                  </div>
                  <div className="text-[#4A6080] text-xs mt-0.5">
                    {storage.priceNote}
                  </div>
                </div>
                <Link
                  href={t["types.more.href"]}
                  className="px-4 py-2 rounded-lg text-xs font-semibold btn-secondary"
                >
                  {t["types.more.label"]}
                </Link>
              </div>
            </div>
          ))}
        </div>

        {/* Comparison table */}
        <div className="reveal">
          <h3 className="font-display font-bold text-2xl text-white mb-6 text-center">
            {t["table.title"]}
          </h3>
          <div className="glass rounded-2xl overflow-hidden neon-border">
            <div className="overflow-x-auto">
              <table className="w-full data-table min-w-[700px]">
                <thead>
                  <tr>
                    <th className="text-left">{t["table.head.param"]}</th>
                    <th className="text-center" style={{ color: "#4F7CFF" }}>{t["table.head.block"]}</th>
                    <th className="text-center" style={{ color: "#7C3AED" }}>{t["table.head.file"]}</th>
                    <th className="text-center" style={{ color: "#00FF88" }}>{t["table.head.object"]}</th>
                  </tr>
                </thead>
                <tbody>
                  {[
                    ...tableRows,
                    {
                      param: t["table.price.param"],
                      block: prices.block ?? missing,
                      file: prices.file ? `${t["table.price.from"]}${prices.file}` : missing,
                      object: prices.object ?? missing,
                    },
                  ].map((row) => (
                    <tr key={row.param}>
                      <td className="text-[#E8F0FF] font-medium">{row.param}</td>
                      <td className="text-center text-[#4F7CFF]">{row.block}</td>
                      <td className="text-center text-[#7C3AED]">{row.file}</td>
                      <td className="text-center text-[#00FF88]">{row.object}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}